import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { CheckedConditionalMenu, MenuData } from 'src/models';
import { IStoreState } from 'src/store';
import { ViewMode } from 'src/enums';
import * as acBase from 'src/store/states/GenericActionCreator';

/** Interfaces */
export interface IMenusState {
    allMenus: MenuData;
    selectedScheduleOption: string;
    selectedMileageOption: string;
    checkedMileageMenu: string;
    checkedConditionalMenus: CheckedConditionalMenu[];
    menusBuilt: boolean;
}

export const initialMenuData: MenuData = {
    menus: [],
    scheduleOptions: [],
    mileageOptions: [],
    defaultScheduleOption: '',
    defaultMileageOption: '',
    returnCode: '',
};

/** Initial State */
export const initialState: IMenusState = {
    allMenus: initialMenuData,
    selectedScheduleOption: '',
    selectedMileageOption: '',
    checkedMileageMenu: '',
    checkedConditionalMenus: [],
    menusBuilt: false,
};

export const MaintenanceMenusSlice = createSlice({
    name: 'Menus',
    initialState: initialState,
    reducers: {
        receiveMenus: (state, action: PayloadAction<MenuData>) => {
            state.allMenus.menus = action.payload.menus ?? [];
            state.allMenus.defaultMileageOption = action.payload.defaultMileageOption ?? '';
            state.allMenus.defaultScheduleOption = action.payload.defaultScheduleOption ?? '';
            state.allMenus.mileageOptions = action.payload.mileageOptions ?? [];
            state.allMenus.scheduleOptions = action.payload.scheduleOptions ?? [];
            state.allMenus.returnCode = action.payload.returnCode ?? '';
            state.checkedMileageMenu = '';
            state.checkedConditionalMenus = [];
            state.selectedScheduleOption = '';
            state.selectedMileageOption = '';
        },
        toggleMileageMenu: (state, action: PayloadAction<string>) => {
            state.checkedMileageMenu = action.payload;
        },
        toggleConditionalMenu: (state, action: PayloadAction<CheckedConditionalMenu[]>) => {
            state.checkedConditionalMenus = action.payload;
        },
        setScheduleOption: (state, action: PayloadAction<string>) => {
            state.selectedScheduleOption = action.payload;
            state.selectedMileageOption = '';
        },
        setMileageOption: (state, action: PayloadAction<string>) => {
            state.selectedMileageOption = action.payload;
        },
        clearMenus: (state) => {
            state.allMenus = initialMenuData;
            state.checkedConditionalMenus = [];
            state.checkedMileageMenu = '';
            state.selectedMileageOption = '';
            state.selectedScheduleOption = '';
            state.menusBuilt = false;
        },
        clearMenusChecks: (state) => {
            state.checkedConditionalMenus = [];
            state.checkedMileageMenu = '';
        },
        setMenuError: (state, action: PayloadAction<string>) => {
            state.allMenus.returnCode = action.payload;
        },
        setMenusBuilt: (state, action: PayloadAction<boolean>) => {
            state.menusBuilt = action.payload;
        },
    },
});

export const {
    receiveMenus,
    toggleMileageMenu,
    toggleConditionalMenu,
    setScheduleOption,
    setMileageOption,
    clearMenus,
    clearMenusChecks,
    setMenuError,
    setMenusBuilt,
} = MaintenanceMenusSlice.actions;

export default MaintenanceMenusSlice.reducer;

export function GetMenus(scheduleOption, mileageOption) {
    return async (dispatch, getState: () => IStoreState) => {
        dispatch(setMenusBuilt(false));
        const selectedVehicle = getState().Vehicles?.selectedVehicle!;
        const prevMileage = getState().Vehicles?.vehicles[selectedVehicle]?.prevMileage;
        const TMNAMileage = getState().AdditionalServices.maintContractData.TMNAMileage;
        const apptVehVin = getState().Appointment.ApptVeh.vin;
        const apptVehMake = getState().Appointment.ApptVeh.make;
        const apptVehYear = getState().Appointment.ApptVeh.vehYr;
        const apptVehModel = getState().Appointment.ApptVeh.model;
        const apptVehMileage = getState().Appointment.ApptVeh.mileage !== -1 ?
            getState().Appointment.ApptVeh.mileage : TMNAMileage > prevMileage ? TMNAMileage : (prevMileage ? prevMileage : 0);
        const specProfile = getState().Framework.specProfile;
        const store = getState().Framework.store;
        const branch = getState().Framework.branch;
        const logId = getState().Specs.specs?.logId;
        const vinOwned = getState().Vehicles.decodedData?.decodeErr;
        let callingProgram = 'SVP';
        const viewMode = getState().Framework.viewMode;
        if (viewMode === ViewMode.ServiceDocs || viewMode === ViewMode.ViewServiceHist) {
            callingProgram = 'OSD';
        }
        const reqData = {
            keyVin: apptVehVin,
            keyMake: apptVehMake,
            keyYear: apptVehYear,
            keyModel: apptVehModel,
            keyMileage: apptVehMileage,
            selectedScheduleOption: scheduleOption,
            selectedMileageOption: mileageOption,
            profileNum: specProfile,
            keyCustNum: selectedVehicle,
            logId: logId,
            callingProgram: callingProgram,
            vinOwned: vinOwned,
        };
        const response = await acBase.eraWebServiceCall<MenuData | AxiosError>('SVPController', 'GetMenus', reqData, store, branch);
        if (response.returnCode && response.returnCode === '8000') {
            dispatch(setMenuError(response.returnCode));
        } else {
            dispatch(receiveMenus(response));
            dispatch(setMenusBuilt(true));
        }
    };
}

export function ToggleMenu(id) {
    return (dispatch, getState: () => IStoreState) => {
        const allMenus = [...getState().Menus.allMenus.menus];
        const menuIndex = allMenus.findIndex(result => result.id === id);
        if (allMenus[menuIndex].type === 'mileage') {
            let checkedMileageMenu = getState().Menus.checkedMileageMenu;
            if (checkedMileageMenu === id) {
                checkedMileageMenu = '';
            } else {
                checkedMileageMenu = id;
            }
            dispatch(toggleMileageMenu(checkedMileageMenu));
        } else {
            let checkedConditionalMenus = [...getState().Menus.checkedConditionalMenus];
            const alreadyChecked = checkedConditionalMenus.findIndex(result => result.id === id);
            if (alreadyChecked === -1) {
                checkedConditionalMenus.push({ id: id });
            } else {
                checkedConditionalMenus = checkedConditionalMenus.filter(result => result.id !== id);
            }
            dispatch(toggleConditionalMenu(checkedConditionalMenus));
        }
    };
}

export function SetScheduleOption(value) {
    return (dispatch) => {
        dispatch(setScheduleOption(value));
    };
}

export function SetMileageOption(value) {
    return (dispatch, getState: () => IStoreState) => {
        const mileageMenus = [...getState().Menus.allMenus.mileageOptions];
        const checkValue = mileageMenus.findIndex(option => option.value === value);
        if (checkValue === -1){
            dispatch(setMileageOption(''));
        } else {
            dispatch(setMileageOption(value));
        }
    };
}

export function ClearMenusState() {
    return (dispatch) => {
        dispatch(clearMenus());
    };
}

export function ClearMenusChecks() {
    return (dispatch) => {
        dispatch(clearMenusChecks());
    };
}
